import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { useClaimsStore } from '@/stores/claims-store';
import { trackPackGenerated } from '@/lib/analytics';

interface PaymentSuccessProps {
  isOpen: boolean;
  onClose: () => void;
  packType: 'council' | 'court';
  claimId: string;
}

type GenerationStep = 'payment' | 'evidence' | 'documents' | 'complete';

export function PaymentSuccess({ isOpen, onClose, packType, claimId }: PaymentSuccessProps) {
  const [showConfetti, setShowConfetti] = useState(false);
  const [currentStep, setCurrentStep] = useState<GenerationStep>('payment');
  const [isComplete, setIsComplete] = useState(false);
  const { fetchPacks, setPackGenerating } = useClaimsStore();

  // Poll for pack completion
  useEffect(() => {
    if (!isOpen || !claimId) return;

    // Set pack as generating in store
    setPackGenerating(true);

    // Simulate progress through steps while polling
    const stepTimers: NodeJS.Timeout[] = [];
    stepTimers.push(setTimeout(() => setCurrentStep('evidence'), 1500));
    stepTimers.push(setTimeout(() => setCurrentStep('documents'), 3000));

    // Start polling for pack completion
    const pollInterval = setInterval(async () => {
      await fetchPacks(claimId);
      const store = useClaimsStore.getState();
      const hasPack = store.packs.some(p => p.packType === packType);
      
      if (hasPack) {
        trackPackGenerated(packType, claimId);
        setCurrentStep('complete');
        setIsComplete(true);
        setPackGenerating(false);
        clearInterval(pollInterval);
      }
    }, 3000);

    // Stop polling after 5 minutes
    const timeout = setTimeout(() => {
      clearInterval(pollInterval);
      setPackGenerating(false);
    }, 300000);

    return () => {
      stepTimers.forEach(clearTimeout);
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [isOpen, claimId, packType, fetchPacks, setPackGenerating]);

  // Handle close - refresh data
  const handleClose = useCallback(() => {
    // Ensure latest packs are fetched when closing
    if (claimId) {
      fetchPacks(claimId);
    }
    onClose();
  }, [claimId, fetchPacks, onClose]);

  useEffect(() => {
    if (isOpen) {
      // Trigger confetti animation
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-brand-950/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Confetti */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(50)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 0.5}s`,
                backgroundColor: ['#0f766e', '#f59e0b', '#10b981', '#6366f1', '#ec4899'][Math.floor(Math.random() * 5)],
                width: `${8 + Math.random() * 8}px`,
                height: `${8 + Math.random() * 8}px`,
                borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              }}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 fade-in duration-300">
        {/* Success Icon */}
        <div className="pt-10 pb-6 text-center">
          <div className="mx-auto w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mb-6 animate-in zoom-in duration-500">
            <svg className="w-10 h-10 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h2 className="font-display text-2xl font-bold text-brand-950 mb-2">
            Payment Successful!
          </h2>
          <p className="text-surface-600 px-6">
            Your {packType === 'council' ? 'Council' : 'Court'} Claim Pack is being generated.
          </p>
        </div>

        {/* Status */}
        <div className="px-6 pb-6">
          <div className={`bg-gradient-to-br ${isComplete ? 'from-success-50 to-brand-50/30 border-success-200' : 'from-brand-50 to-success-50/30 border-brand-100'} rounded-xl p-5 mb-6 border`}>
            <div className="flex items-center gap-3 mb-4">
              {isComplete ? (
                <>
                  <div className="w-10 h-10 bg-success-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-display font-semibold text-success-700">Pack ready!</p>
                    <p className="text-sm text-surface-600">Your claim pack has been generated</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center">
                    <svg className="w-5 h-5 text-brand-600 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-display font-semibold text-brand-950">Generating your pack</p>
                    <p className="text-sm text-surface-600">This usually takes 30-60 seconds</p>
                  </div>
                </>
              )}
            </div>

            {/* Progress steps */}
            <div className="space-y-3 pl-1">
              {/* Step 1: Payment confirmed - always complete */}
              <div className="flex items-center gap-3 text-sm">
                <div className="w-6 h-6 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-surface-700 font-medium">Payment confirmed</span>
              </div>
              
              {/* Step 2: Evidence gathered */}
              <div className="flex items-center gap-3 text-sm">
                {currentStep === 'payment' ? (
                  <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-2.5 h-2.5 bg-brand-500 rounded-full animate-pulse" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                <span className={`font-medium ${currentStep === 'payment' ? 'text-brand-700' : 'text-surface-700'}`}>
                  {currentStep === 'payment' ? 'Gathering evidence...' : 'Evidence gathered'}
                </span>
              </div>
              
              {/* Step 3: Creating documents */}
              <div className="flex items-center gap-3 text-sm">
                {currentStep === 'complete' ? (
                  <div className="w-6 h-6 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0">
                    <svg className="w-3.5 h-3.5 text-success-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                ) : currentStep === 'documents' || currentStep === 'evidence' ? (
                  <div className="w-6 h-6 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-2.5 h-2.5 bg-brand-500 rounded-full animate-pulse" />
                  </div>
                ) : (
                  <div className="w-6 h-6 rounded-full bg-surface-100 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-surface-300 rounded-full" />
                  </div>
                )}
                <span className={`font-medium ${
                  currentStep === 'complete' ? 'text-surface-700' : 
                  (currentStep === 'documents' || currentStep === 'evidence') ? 'text-brand-700' : 'text-surface-400'
                }`}>
                  {currentStep === 'complete' ? 'Professional documents created' : 'Creating professional documents...'}
                </span>
              </div>
            </div>
          </div>

          <p className="text-sm text-surface-500 text-center mb-5">
            {isComplete 
              ? 'Your claim pack is ready to download!'
              : "Mac will notify you when your pack is ready. You can also refresh this page to check."
            }
          </p>

          <Button 
            fullWidth 
            onClick={handleClose} 
            className={`font-semibold ${isComplete ? 'bg-success-600 hover:bg-success-700' : ''}`}
          >
            {isComplete ? 'View & Download Pack' : 'Continue to My Claim'}
          </Button>
        </div>
      </div>

      {/* Confetti animation styles */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-10vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
